/**
 * useClientMutation Hook
 * Feature: 018-edit-client-association - User Story 2
 * 
 * React Query mutation for creating clients with duplicate detection handling
 * Returns warnings array if duplicate email is detected (auto-link behavior)
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createAccount } from '../../../services/accountsApi';
import type { CreateAccountRequest, Account } from '../../../types/account';

interface CreateClientResult {
  account: Account;
  etag: string;
  isDuplicate: boolean;
  warning?: string;
}

export function useClientMutation() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (request: CreateAccountRequest): Promise<CreateClientResult> => {
      const { account, etag } = await createAccount(request);

      // Check if duplicate was detected (warnings array with DUPLICATE_EMAIL)
      const duplicateWarning = account.warnings?.find(
        (w) => w.code === 'DUPLICATE_EMAIL'
      );

      return {
        account,
        etag,
        isDuplicate: !!duplicateWarning,
        warning: duplicateWarning?.message,
      };
    },
    onSuccess: (data) => {
      // Invalidate accounts list cache
      queryClient.invalidateQueries({ queryKey: ['accounts'] });

      // If it's a new account (not duplicate), invalidate the specific account query too
      if (!data.isDuplicate) {
        queryClient.invalidateQueries({ queryKey: ['account', data.account.accountId] });
      }
    },
  });

  return mutation;
}
